import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {
  BokkzBtwDataService,
  BtwAangifte, BtwOverzicht,
  ValueAddedTaxEntity,
} from "@snelstart-ventures/fiscaal/domain-logic";
import {FormArray, FormControl, FormGroup} from "@angular/forms";
import {debounceTime, filter, merge, Observable, shareReplay, take} from "rxjs";
import { SharedUiAlertModule } from '@snelstart/ui-alert';
import { SharedUiFormControlsModule } from '@snelstart/ui-form-controls';
import { SharedUiDetailsModule } from '@snelstart/ui-details';
import { CommonModule, DecimalPipe } from '@angular/common';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

@Component({
  selector: 'snelstart-venture-fiscaal-feature-suppleties',
  templateUrl: './suppleties.component.html',
  styleUrls: ['./suppleties.component.scss'],
  imports: [
    SharedUiAlertModule,
    SharedUiFormControlsModule,
    SharedUiDetailsModule,
    DecimalPipe,
    CommonModule
  ]
})
export class SuppletiesComponent implements OnInit, OnDestroy {

  @Input() public suppletie = true;
  private readonly destroyRef = inject(DestroyRef);
  private readonly dataService = inject(BokkzBtwDataService);

  public index = 0;
  public vatEntity = {} as ValueAddedTaxEntity;
  private previousAangiftesSnapshot: unknown = [];
  public totaalOudeAangifte= 0;
  public totaalNieuweAangifte=  0;

  public btwAangiftenFg = new FormGroup({
    suppleties: new FormArray<FormGroup>([])
  });

  readonly valueAddedTaxEntity$: Observable<ValueAddedTaxEntity> =
    this.dataService.valueAddedTaxEntity.pipe(
      filter((data): data is ValueAddedTaxEntity => !!data?.overzicht),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  readonly periodeIndex$: Observable<number> =
    this.dataService.periodeIndex.pipe(
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  get aangifte(): FormArray<FormGroup> {
    return this.btwAangiftenFg.get('suppleties') as FormArray<FormGroup>;
  }

  createFormGroup(item?: BtwAangifte): FormGroup {
    const formGroup = new FormGroup({
      initialenBelastingplichtige: new FormControl<string | null>(item?.initialenBelastingplichtige ?? null),
      tussenvoegselBelastingplichtige: new FormControl<string | null>(item?.tussenvoegselBelastingplichtige ?? null),
      achternaamBelastingplichtige: new FormControl<string | null>(item?.achternaamBelastingplichtige ?? null),
      omzetbelastingNummer: new FormControl<string | null>(item?.omzetbelastingNummer ?? null),
      beconNummer: new FormControl<string | null>(item?.beconNummer ?? null),
      naamContactPersoon: new FormControl<string | null>(item?.naamContactPersoon ?? null),
      telefoonnummer: new FormControl<string | null>(item?.telefoonnummer ?? null),
      functie: new FormControl<string | null>(item?.functie ?? null),
      tijdvak: new FormGroup({
        begindatum: new FormControl<Date | null>(item?.tijdvak?.begindatum ?? null),
        einddatum: new FormControl<Date | null>(item?.tijdvak?.einddatum ?? null),
      }),
      suppletie: new FormGroup({
        suppletieAangifte: new FormControl<boolean | null>(item?.suppletie?.suppletieAangifte ?? false),
        totaalNieuweAangifte: new FormControl<number | null>(item?.suppletie?.totaalNieuweAangifte ?? null),
        totaalOudeAangifte: new FormControl<number | null>(item?.suppletie?.totaalOudeAangifte ?? null),
      }),
      omzetHoog: new FormGroup({
        omzet: new FormControl<number | null>(item?.omzetHoog?.omzet ?? null),
        omzetbelasting: new FormControl<number | null>(item?.omzetHoog?.omzetbelasting ?? null),
      }),
      omzetLaag: new FormGroup({
        omzet: new FormControl<number | null>(item?.omzetLaag?.omzet ?? null),
        omzetbelasting: new FormControl<number | null>(item?.omzetLaag?.omzetbelasting ?? null),
      }),
      omzetOverigTarief: new FormGroup({
        omzet: new FormControl<number | null>(item?.omzetOverigTarief?.omzet ?? null),
        omzetbelasting: new FormControl<number | null>(item?.omzetOverigTarief?.omzetbelasting ?? null),
      }),
      privegebruik: new FormGroup({
        omzet: new FormControl<number | null>(item?.privegebruik?.omzet ?? null),
        omzetbelasting: new FormControl<number | null>(item?.privegebruik?.omzetbelasting ?? null),
      }),
      omzetNul: new FormControl<number | null>(item?.omzetNul ?? null),
      omzetVerlegdBinnenland: new FormGroup({
        omzet: new FormControl<number | null>(item?.omzetVerlegdBinnenland?.omzet ?? null),
        omzetbelasting: new FormControl<number | null>(item?.omzetVerlegdBinnenland?.omzetbelasting ?? null),
      }),
      leveringenNaarLandenBuitenDeEU: new FormControl<number | null>(item?.leveringenNaarLandenBuitenDeEU ?? null),
      leveringenNaarLandenBinnenDeEU: new FormControl<number | null>(item?.leveringenNaarLandenBinnenDeEU ?? null),
      installatieOfAfstandsverkopenBinnenDeEU: new FormControl<number | null>(item?.installatieOfAfstandsverkopenBinnenDeEU ?? null),
      leveringenUitLandenBuitendeEU: new FormGroup({
        omzet: new FormControl<number | null>(item?.leveringenUitLandenBuitendeEU?.omzet ?? null),
        omzetbelasting: new FormControl<number | null>(item?.leveringenUitLandenBuitendeEU?.omzetbelasting ?? null),
      }),
      leveringenUitLandenBinnenDeEU: new FormGroup({
        omzet: new FormControl<number | null>(item?.leveringenUitLandenBinnenDeEU?.omzet ?? null),
        omzetbelasting: new FormControl<number | null>(item?.leveringenUitLandenBinnenDeEU?.omzetbelasting ?? null),
      }),
      verschuldigdeOmzetbelasting: new FormControl<number | null>(item?.verschuldigdeOmzetbelasting ?? null),
      voorbelasting: new FormControl<number | null>(item?.voorbelasting ?? null),
      verminderingVolgensDeKleineondernemersregeling: new FormControl<number | null>(item?.verminderingVolgensDeKleineondernemersregeling ?? null),
      totaal: new FormControl<number | null>(item?.totaal ?? null),
      betalingskenmerk: new FormControl<string | null>(item?.betalingskenmerk ?? null),
      verzendDatum: new FormControl<Date | null>(item?.verzendDatum ?? null),
      uitersteBetaaldatum: new FormControl<Date | null>(item?.uitersteBetaaldatum ?? null),
    });

    this.applyReadonlyState(formGroup);
    return formGroup;
  }

  get suppleties(): FormArray {
    return this.btwAangiftenFg.get('suppleties') as FormArray;
  }

  getTotaalSuppletie(index : number) {
    const eerder = this.vatEntity.overzicht?.btwSuppleties?.at(index)?.suppletie?.totaalOudeAangifte;
    const nieuw = this.vatEntity.overzicht?.btwSuppleties?.at(index)?.suppletie?.totaalNieuweAangifte;
    this.totaalOudeAangifte = eerder || 0;
    this.totaalNieuweAangifte = nieuw || 0;
  }

  ngOnInit(): void {
    this.periodeIndex$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((index) => {
      this.index = index;
      this.getTotaalSuppletie(index);
    });

    this.valueAddedTaxEntity$.pipe(
      take(1),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((data) => {
      this.updateForm(data);
      this.getTotaalSuppletie(this.index);
    });

    merge(this.btwAangiftenFg.valueChanges).pipe(
      debounceTime(300),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      const currentAangiftes = this.btwAangiftenFg.getRawValue().suppleties;
      const shouldRecalculate = this.hasRelevantNumberChange(this.previousAangiftesSnapshot, currentAangiftes);
      this.saveForm();
      if (shouldRecalculate) {
        this.dataService.calculateLatestValueAddedTaxEntity().pipe(
          takeUntilDestroyed(this.destroyRef)
        ).subscribe();
      }
      this.previousAangiftesSnapshot = this.cloneSnapshot(currentAangiftes);
    });
  }

  calculateTotal(index: number): any {
    const aangifte = this.suppleties.at(index).getRawValue() as BtwAangifte;
    const btwhoog = aangifte.omzetHoog?.omzetbelasting ?? 0;
    const btwlaag = aangifte.omzetLaag?.omzetbelasting ?? 0;
    const btwoverig = aangifte.omzetOverigTarief?.omzetbelasting ?? 0;
    const btwprive = aangifte.privegebruik?.omzetbelasting ?? 0;
    const btwverlegdbinnenland = aangifte.omzetVerlegdBinnenland?.omzetbelasting ?? 0;
    const btwleveringenuitbuitendeeu = aangifte.leveringenUitLandenBuitendeEU?.omzetbelasting ?? 0;
    const btwleveringenuitbinnenDeEU = aangifte.leveringenUitLandenBinnenDeEU?.omzetbelasting ?? 0;
    const voorbelasting = aangifte.voorbelasting ?? 0;
    const kor = aangifte.verminderingVolgensDeKleineondernemersregeling ?? 0;
    const sum = btwhoog + btwlaag + btwoverig + btwoverig + btwprive + btwverlegdbinnenland
      + btwleveringenuitbuitendeeu + btwleveringenuitbinnenDeEU - voorbelasting - kor ;
    this.suppleties.at(index).get('suppletie.totaalNieuweAangifte')?.setValue(sum, {emitEvent: false});
    return sum;
  }

  private updateForm(value: ValueAddedTaxEntity): void {
    this.vatEntity = value;

    const lijst = value.overzicht?.btwSuppleties || [];
    const formArray = this.aangifte;

    while (formArray.length > lijst.length) {
      formArray.removeAt(formArray.length - 1, {emitEvent: false});
    }

    for (let i = 0; i < lijst.length; i++) {
      const row = {
        ...lijst[i],
      };

      if (i < formArray.length) {
        const control = formArray.at(i) as FormGroup;
        control.patchValue(row, {emitEvent: false});
        this.applyReadonlyState(control);
      } else {
        formArray.push(this.createFormGroup(row), {emitEvent: false});
      }
    }
    this.previousAangiftesSnapshot = this.cloneSnapshot(this.btwAangiftenFg.getRawValue().suppleties);
  }

  private applyReadonlyState(formGroup: FormGroup): void {
    formGroup.get('tijdvak')?.disable({emitEvent: false});
    formGroup.get('betalingskenmerk')?.disable({emitEvent: false});
    formGroup.get('totaal')?.disable({emitEvent: false});
    formGroup.get('verzendDatum')?.disable({emitEvent: false});
  }

  ngOnDestroy(): void {
    this.saveForm();
  }

  saveForm(): void {
    this.btwAangiftenFg.updateValueAndValidity({emitEvent: false});
    const aangiftes = (this.btwAangiftenFg.getRawValue().suppleties ?? []) as BtwAangifte[];
    this.dataService.patchValueAddedTaxEntity(current => {
      if (!current.overzicht) {
        return current;
      }

      return {
        ...current,
        overzicht: {
          ...current.overzicht,
          btwSuppleties: aangiftes
        } as BtwOverzicht
      } as ValueAddedTaxEntity;
    });
  }

  private cloneSnapshot<T>(value: T): T {
    return JSON.parse(JSON.stringify(value)) as T;
  }

  private hasRelevantNumberChange(previous: unknown, current: unknown, fieldKey?: string): boolean {
    if (Array.isArray(previous) || Array.isArray(current)) {
      const prevArray = Array.isArray(previous) ? previous : [];
      const currArray = Array.isArray(current) ? current : [];
      const maxLength = Math.max(prevArray.length, currArray.length);

      for (let i = 0; i < maxLength; i++) {
        if (this.hasRelevantNumberChange(prevArray[i], currArray[i], fieldKey)) {
          return true;
        }
      }
      return false;
    }

    const prevIsObject = typeof previous === 'object' && previous !== null;
    const currIsObject = typeof current === 'object' && current !== null;

    if (prevIsObject || currIsObject) {
      const prevObject = (prevIsObject ? previous : {}) as Record<string, unknown>;
      const currObject = (currIsObject ? current : {}) as Record<string, unknown>;
      const keys = new Set([...Object.keys(prevObject), ...Object.keys(currObject)]);

      for (const key of keys) {
        if (this.hasRelevantNumberChange(prevObject[key], currObject[key], key)) {
          return true;
        }
      }
      return false;
    }

    if (previous === current) {
      return false;
    }

    const previousIsNumber = this.isNumericLike(previous);
    const currentIsNumber = this.isNumericLike(current);

    if (previousIsNumber || currentIsNumber) {
      return fieldKey !== 'omzet';
    }

    return false;
  }

  private isNumericLike(value: unknown): boolean {
    if (typeof value === 'number') {
      return !Number.isNaN(value);
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      return trimmed.length > 0 && !Number.isNaN(Number(trimmed));
    }

    return false;
  }


}
